import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { MonitorPlay, Plus, Trash2, X } from "lucide-react";

export default function AdsPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [ads, setAds] = useState([]);
  const [modal, setModal] = useState(false);
  const [display, setDisplay] = useState(false);
  const [slide, setSlide] = useState(0);
  const [form, setForm] = useState({ title: "", message: "", image_url: "" });

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!display) return;
    const active = ads.filter((a) => a.active);
    if (active.length === 0) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % active.length), 6000);
    return () => clearInterval(t);
  }, [display, ads]);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API_URL}/ads`, { headers, withCredentials: true });
      setAds(res.data);
    } catch { toast.error("Erro ao carregar anúncios"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/ads`, { ...form, image_url: form.image_url || null }, { headers, withCredentials: true });
      toast.success("Anúncio criado");
      setModal(false); setForm({ title: "", message: "", image_url: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const toggle = async (a) => {
    await axios.put(`${API_URL}/ads/${a.ad_id}/toggle`, {}, { headers, withCredentials: true });
    fetchAll();
  };

  const remove = async (a) => {
    if (!window.confirm(`Remover anúncio "${a.title}"?`)) return;
    await axios.delete(`${API_URL}/ads/${a.ad_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const activeAds = ads.filter((a) => a.active);
  const current = activeAds[slide % Math.max(activeAds.length, 1)];

  if (display) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black text-white" data-testid="ad-display-mode">
        <button onClick={() => setDisplay(false)} className="absolute right-6 top-6 rounded-full border border-white/20 p-2 text-zinc-400 hover:text-white" data-testid="exit-display-btn">
          <X className="w-6 h-6" />
        </button>
        {current ? (
          <div className="max-w-4xl px-10 text-center">
            {current.image_url && <img src={current.image_url} alt={current.title} className="mx-auto mb-8 max-h-[50vh] rounded-2xl object-contain" />}
            <h1 className="text-5xl font-black lg:text-7xl">{current.title}</h1>
            {current.message && <p className="mt-6 text-2xl text-zinc-300">{current.message}</p>}
            <div className="mt-10 flex justify-center gap-2">
              {activeAds.map((_, i) => (
                <span key={i} className={`h-2 w-2 rounded-full ${i === slide % activeAds.length ? "bg-cyan-400" : "bg-zinc-700"}`} />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-zinc-500">Sem anúncios ativos</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Publicidade no Ecrã</h1>
          <p className="text-muted-foreground">Anúncios para o ecrã voltado ao cliente (promoções, mensagens, campanhas)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setSlide(0); setDisplay(true); }} disabled={activeAds.length === 0} data-testid="open-display-btn">
            <MonitorPlay className="w-4 h-4 mr-2" /> Abrir Modo Ecrã
          </Button>
          <Button onClick={() => setModal(true)} data-testid="add-ad-btn"><Plus className="w-4 h-4 mr-2" /> Novo Anúncio</Button>
        </div>
      </div>

      {ads.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><MonitorPlay className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhum anúncio criado</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {ads.map((a) => (
            <Card key={a.ad_id} data-testid={`ad-${a.ad_id}`}>
              <CardContent className="p-4 space-y-2">
                {a.image_url && <img src={a.image_url} alt={a.title} className="h-28 w-full rounded-lg object-cover" />}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{a.title}</p>
                    {a.message && <p className="text-xs text-muted-foreground line-clamp-2">{a.message}</p>}
                  </div>
                  <Badge className={a.active ? "bg-emerald-500/20 text-emerald-600" : "bg-slate-500/20 text-slate-500"}>{a.active ? "Ativo" : "Inativo"}</Badge>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <Switch checked={a.active} onCheckedChange={() => toggle(a)} data-testid={`toggle-ad-${a.ad_id}`} />
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(a)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Anúncio</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="space-y-1"><Label>Título *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Happy Hour 17h-19h!" data-testid="ad-title-input" /></div>
            <div className="space-y-1"><Label>Mensagem</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Detalhes da promoção ou mensagem" /></div>
            <div className="space-y-1"><Label>URL da Imagem (opcional)</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-ad-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
